/**
 * Live capacity/status controller for the venue booking wizard.
 */
(function (global) {
  'use strict';

  var UNKNOWN_CAPACITY_MAX_QTY = 1;
  var DEFAULT_POLL_INTERVAL_MS = 5000;
  var MOBILE_POLL_INTERVAL_MS = 15000;

  function cityEvents() {
    return global.WorkshopCityEvents || null;
  }

  function maxBookableQty(slot) {
    if (!slot) return 0;
    var api = cityEvents();
    if (api && !api.isSlotBookable(slot)) return 0;
    var cap = slot.capacity_remaining;
    if (cap == null || cap === '') {
      cap = slot._raw && slot._raw.capacity_remaining;
    }
    if (cap == null || cap === '') return UNKNOWN_CAPACITY_MAX_QTY;
    var n = Number(cap);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function enrichSlots(slots, options) {
    var api = cityEvents();
    var list = Array.isArray(slots) ? slots : [];
    if (!api || typeof api.mergeSlotsWithLive !== 'function') return Promise.resolve(list);
    var fetchOpts = Object.assign({ fetch: true }, options || {});
    return Promise.resolve(api.mergeSlotsWithLive(list, fetchOpts)).then(function (merged) {
      if (options) options._liveOk = fetchOpts._liveOk;
      return merged;
    });
  }

  function ensureFreshSlot(slot, options) {
    if (!slot) return Promise.resolve(null);
    return enrichSlots([slot], options).then(function (slots) {
      var fresh = slots && slots[0] ? slots[0] : slot;
      var api = cityEvents();
      if (api && !api.isSlotBookable(fresh)) return null;
      return fresh;
    });
  }

  function isMobilePollViewport() {
    try {
      return !!(global.matchMedia && global.matchMedia('(max-width: 749px)').matches);
    } catch (_) {
      return false;
    }
  }

  function readPollInterval(root, config) {
    config = config || {};
    var api = cityEvents();
    if (api && typeof api.resolvePollIntervalMs === 'function') {
      // Pass 0 (not null) as the fallback: resolvePollIntervalMs returns its
      // internal 5000 default when the fallback is null, which would masquerade
      // as real config and clobber the mobile default below. With 0 it returns
      // 0 when unconfigured, so we correctly fall through to the viewport default.
      var fromConfig = api.resolvePollIntervalMs('booking', 0);
      if (fromConfig > 0) return fromConfig;
    }
    if (config.pollIntervalMs != null) {
      var fromOpt = Number(config.pollIntervalMs) || 0;
      if (fromOpt > 0) return fromOpt;
    }
    var attr = root && root.getAttribute && root.getAttribute('data-wbw-live-poll-interval');
    if (attr != null && attr !== '') {
      var fromAttr = Number(attr) || 0;
      if (fromAttr > 0) return fromAttr;
    }
    if (global.__WBW_LIVE_POLL_INTERVAL_MS__ != null) {
      var fromGlobal = Number(global.__WBW_LIVE_POLL_INTERVAL_MS__) || 0;
      if (fromGlobal > 0) return fromGlobal;
    }
    if (isMobilePollViewport()) return MOBILE_POLL_INTERVAL_MS;
    return DEFAULT_POLL_INTERVAL_MS;
  }

  function liveOptions(root, config) {
    config = config || {};
    return {
      root: root,
      productHandle:
        config.productHandle ||
        (root && root.getAttribute && root.getAttribute('data-product-handle')) ||
        '',
      shopDomain:
        config.shopDomain ||
        (global.Shopify && global.Shopify.shop ? String(global.Shopify.shop) : ''),
    };
  }

  function validateSlotForCart(slot, requestedAddQty, options) {
    var api = cityEvents();
    if (!api || typeof api.validateAddToCart !== 'function') {
      var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
      if (CFG && typeof CFG.verifySoftMode === 'function' && CFG.verifySoftMode()) {
        return Promise.resolve({ allowed: true, skipped_validation: true });
      }
      return Promise.resolve({
        allowed: false,
        message: 'We could not verify workshop availability. Please try again.',
      });
    }
    var opts = Object.assign({ intent: 'add_to_cart' }, options || {});
    return api.validateAddToCart(slot, requestedAddQty, opts);
  }

  function createController(root, config) {
    config = config || {};
    var timer = null;
    var stopped = false;
    var opts = liveOptions(root, config);
    var interval = readPollInterval(root, config);

    var pollGetSlots = null;
    var pollOnUpdate = null;
    var tickInFlight = false;
    var pendingForceTick = false;
    var visibilityBound = false;
    var streamPaused = false;

    var api = cityEvents();
    if (api && typeof api.ensureBookingConfig === 'function') {
      api.ensureBookingConfig(opts).then(function () {
        if (typeof api.resolvePollIntervalMs === 'function') {
          var next = api.resolvePollIntervalMs('booking', interval);
          if (next > 0 && next !== interval) {
            interval = next;
            // Do not resurrect the interval while paused for SSE; startPoll is
            // a no-op timer-wise when streamPaused, and resumeFromStream will
            // apply the updated interval on reconnect fallback.
            if (pollGetSlots && !streamPaused) startPoll(pollGetSlots, pollOnUpdate, { immediate: false });
          }
        }
      });
    }

    function tickNow(getSlots, onUpdate, tickOpts) {
      tickOpts = tickOpts || {};
      var force = tickOpts.force === true;
      if (stopped || typeof getSlots !== 'function') return Promise.resolve({ ok: false });
      if (!force && global.document && global.document.hidden) return Promise.resolve({ ok: false });
      var slots = getSlots() || [];
      if (!slots.length) return Promise.resolve({ ok: true });
      if (tickInFlight) {
        if (force) pendingForceTick = true;
        return Promise.resolve({ ok: false, deferred: true });
      }
      tickInFlight = true;
      var enrichOpts = Object.assign({}, opts, force ? { force: true } : {});
      return enrichSlots(slots, enrichOpts)
        .then(function (freshSlots) {
          if (!stopped && typeof onUpdate === 'function') {
            onUpdate(freshSlots || slots, { at: Date.now() });
          }
          return { ok: enrichOpts._liveOk !== false, slots: freshSlots };
        })
        .catch(function () {
          return { ok: false };
        })
        .finally(function () {
          tickInFlight = false;
          if (pendingForceTick && !stopped && pollGetSlots && pollOnUpdate) {
            pendingForceTick = false;
            tickNow(pollGetSlots, pollOnUpdate, { force: true });
          }
        });
    }

    function startPoll(getSlots, onUpdate, pollOptions) {
      pollOptions = pollOptions || {};
      var immediate = pollOptions.immediate !== false;
      stopPoll();
      pollGetSlots = getSlots;
      pollOnUpdate = onUpdate;
      // While the SSE stream drives updates, keep slot refs but run no timer/tick.
      if (streamPaused) return null;
      if (immediate) tickNow(getSlots, onUpdate);
      if (!(interval > 0)) return null;
      timer = global.setInterval(function () {
        tickNow(getSlots, onUpdate);
      }, interval);
      if (global.document && !visibilityBound) {
        visibilityBound = true;
        global.document.addEventListener('visibilitychange', function () {
          if (global.document.hidden) return;
          if (stopped || !pollGetSlots || !pollOnUpdate) return;
          // SSE drives updates while paused; the wizard rebinds/refreshes the
          // stream on focus, so a hot force-tick here would just double-fetch.
          if (streamPaused) return;
          tickNow(pollGetSlots, pollOnUpdate, { force: true });
        });
      }
      return timer;
    }

    function stopPoll() {
      if (timer) {
        global.clearInterval(timer);
        timer = null;
      }
    }

    return {
      enrichSlots: function (slots, extra) {
        return enrichSlots(slots, Object.assign({}, opts, extra || {}));
      },
      ensureFreshSlot: function (slot) {
        return ensureFreshSlot(slot, opts);
      },
      validateSlotForCart: function (slot, requestedAddQty, extra) {
        return validateSlotForCart(slot, requestedAddQty, Object.assign({}, opts, extra || {}));
      },
      maxBookableQty: maxBookableQty,
      startPoll: startPoll,
      pauseForStream: function () {
        streamPaused = true;
        stopPoll();
      },
      resumeFromStream: function () {
        if (!streamPaused) return;
        streamPaused = false;
        if (pollGetSlots && pollOnUpdate) startPoll(pollGetSlots, pollOnUpdate, { immediate: false });
      },
      restartPoll: function (pollOptions) {
        if (pollGetSlots && pollOnUpdate) startPoll(pollGetSlots, pollOnUpdate, pollOptions);
      },
      tickNow: function (tickOpts) {
        if (pollGetSlots && pollOnUpdate) return tickNow(pollGetSlots, pollOnUpdate, tickOpts);
        return Promise.resolve({ ok: false });
      },
      stop: function () {
        stopped = true;
        stopPoll();
      },
    };
  }

  global.WorkshopBookingWizardLive = {
    createController: createController,
    enrichSlots: enrichSlots,
    ensureFreshSlot: ensureFreshSlot,
    validateSlotForCart: validateSlotForCart,
    maxBookableQty: maxBookableQty,
    startPoll: function (slots, onUpdate, options) {
      var root = options && options.root;
      return createController(root, options).startPoll(
        function () {
          return typeof slots === 'function' ? slots() : slots;
        },
        onUpdate
      );
    },
  };
})(typeof window !== 'undefined' ? window : this);
